import React from 'react';

/**
 * AssessmentStep1FacilityInfo
 *
 * Props:
 * - facilityInfo: object containing facility fields
 * - handleInputChange: function(field) => event handler for controlled inputs
 * - majorIndustries: array of major industry strings
 */
const AssessmentStep1FacilityInfo = ({
  facilityInfo,
  handleInputChange,
  majorIndustries = []
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
        Facility Information
      </h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        className="w-full"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Company Name *
            </label>
            <input
              type="text"
              value={facilityInfo.companyName}
              onChange={handleInputChange('companyName')}
              placeholder="Enter your company name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Contact Name *
            </label>
            <input
              type="text"
              value={facilityInfo.contactName}
              onChange={handleInputChange('contactName')}
              placeholder="Enter contact person name"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={facilityInfo.email}
              onChange={handleInputChange('email')}
              placeholder="Enter email address"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              State/Province *{' '}
              <span className="text-xs text-blue-600 dark:text-blue-400">
                (for grants matching)
              </span>
            </label>
            <select
              value={facilityInfo.state}
              onChange={handleInputChange('state')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
              required
            >
              <option value="">Select state/province...</option>
              <option value="ACT">Australian Capital Territory</option>
              <option value="NSW">New South Wales</option>
              <option value="NT">Northern Territory</option>
              <option value="QLD">Queensland</option>
              <option value="SA">South Australia</option>
              <option value="TAS">Tasmania</option>
              <option value="VIC">Victoria</option>
              <option value="WA">Western Australia</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Industry Sector *{' '}
              <span className="text-xs text-gray-500 dark:text-gray-400">
                (NAICS & ANZSIC)
              </span>
            </label>
            <select
              value={(() => {
                // Convert industry name back to "id::region" format for select value
                if (!facilityInfo.majorIndustry) return '';
                // Use region from facilityInfo to find the correct industry (important for same-name industries in different regions)
                const currentRegion = (facilityInfo.region || 'naics').toLowerCase();
                const selectedIndustry = majorIndustries.find(ind => {
                  if (typeof ind === 'string') return false;
                  const name = ind.name || '';
                  const indRegion = (ind.region || 'naics').toLowerCase();
                  // Match both name AND region to get the correct industry
                  return name === facilityInfo.majorIndustry && indRegion === currentRegion;
                });
                // Fallback: if not found with region, try without region (for backward compatibility)
                const fallbackIndustry = selectedIndustry || majorIndustries.find(ind => {
                  const name = typeof ind === 'string' ? ind : ind.name;
                  return name === facilityInfo.majorIndustry;
                });
                if (fallbackIndustry && typeof fallbackIndustry !== 'string') {
                  const region = (fallbackIndustry.region || currentRegion || 'naics').toLowerCase();
                  const industryId = fallbackIndustry.id || '';
                  return industryId ? `${industryId}::${region}` : `${facilityInfo.majorIndustry}::${region}`;
                }
                return facilityInfo.majorIndustry;
              })()}
              onChange={handleInputChange('majorIndustry')}
              className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-700 dark:focus:ring-emerald-500"
            >
              <option value="">Select your industry sector...</option>
              {majorIndustries && majorIndustries.length > 0 ? (
                (() => {
                  // Group industries by region (NAICS and ANZSIC)
                  const naicsIndustries = majorIndustries.filter(ind => {
                    const region = typeof ind === 'string' ? '' : (ind.region || '').toLowerCase();
                    return region === 'naics';
                  });
                  const anzsicIndustries = majorIndustries.filter(ind => {
                    const region = typeof ind === 'string' ? '' : (ind.region || '').toLowerCase();
                    return region === 'anzsic';
                  });
                  const options = [];
                  
                  // Add NAICS industries
                  if (naicsIndustries.length > 0) {
                    options.push(
                      <optgroup key="naics" label="NAICS (North American Industry Classification System)">
                        {naicsIndustries.map((industry) => {
                          const industryName = typeof industry === 'string' ? industry : industry.name;
                          const industryId = typeof industry === 'string' ? '' : (industry.id || '');
                          const naicsCode = typeof industry === 'string' ? '' : (industry.naics_code || '');
                          const displayName = naicsCode 
                            ? `${industryName} (NAICS: ${naicsCode})` 
                            : `${industryName} (NAICS)`;
                          // Use industryId::region as value to uniquely identify the industry
                          const optionValue = industryId ? `${industryId}::naics` : `${industryName}::naics`;
                          return (
                            <option key={`naics-${industryId || industryName}`} value={optionValue}>
                              {displayName}
                            </option>
                          );
                        })}
                      </optgroup>
                    );
                  }
                  
                  // Add ANZSIC industries
                  if (anzsicIndustries.length > 0) {
                    options.push(
                      <optgroup key="anzsic" label="ANZSIC (Australian and New Zealand Standard Industrial Classification)">
                        {anzsicIndustries.map((industry) => {
                          const industryName = typeof industry === 'string' ? industry : industry.name;
                          const industryId = typeof industry === 'string' ? '' : (industry.id || '');
                          const anzsicCode = typeof industry === 'string' ? '' : (industry.anzsic_code || industry.anszic_code || industry.anszics_code || '');
                          const displayName = anzsicCode 
                            ? `${industryName} (ANZSIC: ${anzsicCode})` 
                            : `${industryName} (ANZSIC)`;
                          // Use industryId::region as value to uniquely identify the industry
                          const optionValue = industryId ? `${industryId}::anzsic` : `${industryName}::anzsic`;
                          return (
                            <option key={`anzsic-${industryId || industryName}`} value={optionValue}>
                              {displayName}
                            </option>
                          );
                        })}
                      </optgroup>
                    );
                  }
                  
                  // If no grouping worked, fallback to original display
                  if (options.length === 0) {
                    return majorIndustries.map((industry) => {
                      const industryName = typeof industry === 'string' ? industry : industry.name;
                      const region = typeof industry === 'string' ? '' : (industry.region || '');
                      const displayName = region ? `${industryName} (${region.toUpperCase()})` : industryName;
                      return (
                        <option key={industryName} value={industryName}>
                          {displayName}
                        </option>
                      );
                    });
                  }
                  
                  return options;
                })()
              ) : (
                <option value="" disabled>Loading industries...</option>
              )}
            </select>
            {majorIndustries && majorIndustries.length === 0 && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                No industries available. Please check your connection or try refreshing the page.
              </p>
            )}
          </div>
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg mt-6">
          <p className="text-sm text-emerald-800 dark:text-emerald-200">
            <strong>📋 About This Assessment:</strong> This assessment will provide customized
            energy efficiency recommendations based on your industry and facility details.
          </p>
        </div>
      </form>
    </div>
  );
};

export default AssessmentStep1FacilityInfo;


